import { useEffect, useLayoutEffect } from 'react';
import { Navigate } from 'react-router-dom';

import SavedEstimateList, {
  SavedEstimateListSkeleton,
} from '~/UI/components/organisms/SavedEstimateList';
import FullPageLoader from '~/UI/components/templates/FullPageLoader';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { StepName } from '~/core/enums';
import { getLocalData, removeLocalData } from '~/core/helpers/storageHelper';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectShowListOfEstimates } from '~/redux/slices/estimate';
import {
  goToStep,
  selectStepState,
  setAllowAutoNavigation,
} from '~/redux/slices/stepper';

const ThankYouPage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const {
    fetchedEstimate,
    isFetchingEstimate,
    isFetchError,
    loadEstimateCode,
  } = useEstimateContext();
  const showListOfEstimates = useAppSelector(selectShowListOfEstimates);
  const { current: currentStep } = useAppSelector(selectStepState);
  const isEstimateCodeSwitch =
    getLocalData(LOCAL_STORAGE_KEY.ESTIMATE_CODE_SWITCH) === 'true';

  const loadEstimateFromURL = new URL(window.location.href).searchParams.get(
    LOCAL_STORAGE_KEY.LOAD_ESTIMATE
  );

  const isWaitingForEstimate =
    (!!loadEstimateFromURL || !!loadEstimateCode) &&
    !showListOfEstimates &&
    !fetchedEstimate &&
    !isFetchError;

  const shouldRedirect =
    !isFetchingEstimate &&
    !isWaitingForEstimate &&
    !isEstimateCodeSwitch &&
    !fetchedEstimate;

  // Set stepper to thankYou and disable auto-navigation when there's an estimate intent.
  // useLayoutEffect ensures Redux updates before navigation provider's useEffect,
  // preventing it from navigating away with stale currentStep = store.
  // setAllowAutoNavigation(false) prevents the stepper listener's resetStepper
  // from overriding currentStep when estimate restoration actions fire.
  useLayoutEffect(() => {
    if (loadEstimateFromURL || loadEstimateCode) {
      dispatch(setAllowAutoNavigation(false));
      dispatch(goToStep(StepName.thankYou));
    }

    return () => {
      dispatch(setAllowAutoNavigation(true));
    };
  }, [dispatch, loadEstimateFromURL, loadEstimateCode]);

  // Reset stepper when redirecting away (prevents nav provider redirect loop)
  useEffect(() => {
    if (shouldRedirect && currentStep === StepName.thankYou) {
      dispatch(goToStep(StepName.store));
    }
  }, [shouldRedirect, currentStep, dispatch]);

  // Clear ESTIMATE_CODE_SWITCH localStorage
  useEffect(() => {
    if (isEstimateCodeSwitch && showListOfEstimates) {
      removeLocalData(LOCAL_STORAGE_KEY.ESTIMATE_CODE_SWITCH);
    }
  }, [isEstimateCodeSwitch, showListOfEstimates]);

  if (isFetchingEstimate || isWaitingForEstimate) {
    return <FullPageLoader />;
  }

  if (isEstimateCodeSwitch) {
    return <SavedEstimateListSkeleton />;
  }

  if (showListOfEstimates && fetchedEstimate) {
    return <SavedEstimateList />;
  }

  // fetchedEstimate is defined but showListOfEstimates hasn't been set yet — wait
  if (fetchedEstimate) {
    return <FullPageLoader />;
  }

  return <Navigate to="/" replace />;
};

export default ThankYouPage;
